import {
  InspectionModel,
  InspectionStatus,
  ProtocolItemModel,
  ProtocolSectionModel,
} from 'shared/domain/inspection/inspectionModel';
import { dtoToModelBase } from 'shared/domain/toBaseModel';
import { userModelToUserWithPersonalData } from 'shared/domain/user/mapping/toView';
import { UserModel } from 'shared/domain/user/types/model';
import { InspectionInDto } from 'shared/dtos/in/inspection';
import { ProtocolItemInDto } from 'shared/dtos/in/protocolItem';
import { ProtocolSectionInDto } from 'shared/dtos/in/protocolSection';
import { findById } from 'shared/utils/id/findById';
import { logError } from 'setup/logger/logError';
import { getProtocolItemEkComment } from 'shared/domain/inspection/protocolItemAccessors';
import { normalizeProtocolItemInDto } from './normalizeProtocolItemInDto';

function protocolItemInDtoToModel(
  protocolItemInDto: ProtocolItemInDto,
  users: UserModel[]
): ProtocolItemModel {
  const normalized = normalizeProtocolItemInDto(protocolItemInDto);
  const {
    _id,
    complianceCheck,
    modifiedAt,
    templateChecklistItem,
    modifiedBy,
    answers,
  } = normalized;

  return {
    _id,
    complianceCheck: complianceCheck!,
    comment: getProtocolItemEkComment(normalized),
    modifiedAt,
    templateChecklistItem,
    answers: answers as ProtocolItemModel['answers'],
    modifiedBy: userModelToUserWithPersonalData(
      findById(users, modifiedBy, 'inspection protocol modifiedBy')
    ),
  };
}

function protocolSectionInDtoToModel(
  protocolSectionInDto: ProtocolSectionInDto,
  users: UserModel[]
): ProtocolSectionModel {
  const {
    _id,
    title,
    checklistSectionId,
    comment,
    description,
    order,
    parentSectionId,
    createdAt,
    createdBy,
    modifiedAt,
    modifiedBy,
  } = protocolSectionInDto;

  return {
    _id,
    title,
    checklistSectionId,
    comment: comment ?? description ?? '',
    order,
    parentSectionId,
    createdAt,
    createdBy: userModelToUserWithPersonalData(
      findById(users, createdBy, 'inspection section createdBy')
    ),
    modifiedAt,
    modifiedBy: userModelToUserWithPersonalData(
      findById(users, modifiedBy, 'inspection section modifiedBy')
    ),
  };
}

function resolveIsCompleted(inspectionDto: InspectionInDto): boolean {
  if (inspectionDto.status == null) {
    // Should be unreachable per the InspectionInDto contract (status is
    // required post-PT-6688) - if it fires, something served an old-shape
    // response (stale cache, rollout skew), and silently defaulting to
    // "not completed" would misrepresent already-completed inspections.
    logError(
      `inspectionDtoToModel: inspection ${inspectionDto._id} is missing 'status' - defaulting isCompleted to false.`
    );
    return false;
  }

  return inspectionDto.status === InspectionStatus.completed;
}

export function inspectionDtoToModel(
  inspectionDto: InspectionInDto,
  users: UserModel[]
): InspectionModel {
  const modelBase = dtoToModelBase({ dto: inspectionDto, users });

  return {
    _id: inspectionDto._id,
    contracts: inspectionDto.contracts,
    controlledParty: inspectionDto.controlledParty,
    createdAt: inspectionDto.createdAt,
    deleted: inspectionDto.deleted,
    inspectionDate: inspectionDto.inspectionDate,
    isCompleted: resolveIsCompleted(inspectionDto),
    levels: inspectionDto.levels,
    modifiedAt: inspectionDto.modifiedAt,
    protocol: inspectionDto.protocol.map((protocolItem) =>
      protocolItemInDtoToModel(protocolItem, users)
    ),
    sections: (inspectionDto.sections || []).map((section) =>
      protocolSectionInDtoToModel(section, users)
    ),
    sites: inspectionDto.sites,
    template: inspectionDto.template,
    workTypes: inspectionDto.workTypes,
    comment: inspectionDto.comment,
    createdBy: userModelToUserWithPersonalData(
      findById(users, inspectionDto.createdBy, 'inspection createdBy')
    ),
    modifiedBy: userModelToUserWithPersonalData(
      findById(users, inspectionDto.modifiedBy, 'inspection modifiedBy')
    ),
  };
}
